import { isEmpty } from "lodash-es";
import { createSelector } from "reselect";

import {
  EntityType,
  EntityTypesSupportingSavedFilters,
  MapFilterEntityType,
} from "@mapmycustomers/shared/types/entity";
import SavedFilter, { MapSavedFilter } from "@mapmycustomers/shared/types/viewModel/SavedFilter";

import { getFavoriteSavedFilterIds, getOrgFavoriteSavedFilterIds } from "../iam";
import { RootState } from "../rootReducer";

// saved filter is suitable if it's type matches given entityType or if it's a Pin SavedFilter and it has
// rules for the given entityType or universal rules and no other rules
const isSuitableForEntityType =
  (entityType: EntityTypesSupportingSavedFilters) =>
  (savedFilter: SavedFilter): boolean => {
    if (entityType === EntityType.ACTIVITY || entityType === EntityType.PIN) {
      return savedFilter.type === entityType;
    }
    // this IF should never be true anymore, since platform doesn't support any other types for SavedFilters
    // except Activity and Pin, but I'm still keeping it here for safety reasons
    if (savedFilter.type !== EntityType.PIN) {
      return false;
    }
    const filters = (savedFilter as MapSavedFilter).filters;
    const usedEntityTypes = (Object.keys(filters) as MapFilterEntityType[]).filter(
      (entityType) => !isEmpty(filters[entityType]?.conditions)
    );

    return (
      (usedEntityTypes.length === 2 &&
        usedEntityTypes.includes("universal") &&
        usedEntityTypes.includes(entityType)) ||
      (usedEntityTypes.length === 1 &&
        (usedEntityTypes.includes("universal") || usedEntityTypes.includes(entityType)))
    );
  };

const savedFiltersState = (state: RootState) => state.savedFilters;

export const getSavedFilters = createSelector(
  [savedFiltersState, (_, entityType: EntityTypesSupportingSavedFilters) => entityType],
  (state, entityType) => {
    const filters = state[entityType === EntityType.ACTIVITY ? entityType : EntityType.PIN]?.list;
    if (entityType === EntityType.ACTIVITY || entityType === EntityType.PIN) {
      return filters;
    }
    const isSuitable = isSuitableForEntityType(entityType);
    return filters?.filter(isSuitable);
  }
);

export const getFavoriteSavedFilters = createSelector(
  [
    savedFiltersState,
    getFavoriteSavedFilterIds,
    getOrgFavoriteSavedFilterIds,
    (_, entityType: EntityTypesSupportingSavedFilters) => entityType,
  ],
  (state, favoriteFilterIds, orgFavoriteFilterIds, entityType) => {
    const isSuitable = isSuitableForEntityType(entityType);
    const targetEntityType = entityType === EntityType.ACTIVITY ? entityType : EntityType.PIN;
    const userFavoriteFilters = state[targetEntityType].list
      .filter((filter) => favoriteFilterIds.includes(filter.id) && isSuitable(filter))
      .map((savedFilter) => ({ ...savedFilter, favoriteSource: "user" }));
    const orgFavoriteFilters = state[targetEntityType].list
      .filter(
        (filter) =>
          orgFavoriteFilterIds.includes(filter.id) &&
          !favoriteFilterIds.includes(filter.id) &&
          isSuitable(filter)
      )
      .map((savedFilter) => ({ ...savedFilter, favoriteSource: "org" }));
    return [...userFavoriteFilters, ...orgFavoriteFilters];
  }
);

// only for user's own favorite saved filters
export const getFavoriteSavedFiltersCount = createSelector(
  [
    savedFiltersState,
    getFavoriteSavedFilterIds,
    (_, entityType: EntityTypesSupportingSavedFilters) => entityType,
  ],
  (state, favoriteFilterIds, entityType) => {
    const isSuitable = isSuitableForEntityType(entityType);
    const targetEntityType = entityType === EntityType.ACTIVITY ? entityType : EntityType.PIN;
    return state[targetEntityType].list.filter(
      (filter) => favoriteFilterIds.includes(filter.id) && isSuitable(filter)
    ).length;
  }
);

// only for user's own favorite saved filters
export const getOrgFavoriteSavedFiltersCount = createSelector(
  [
    savedFiltersState,
    getOrgFavoriteSavedFilterIds,
    (_, entityType: EntityTypesSupportingSavedFilters) => entityType,
  ],
  (state, favoriteFilterIds, entityType) => {
    const isSuitable = isSuitableForEntityType(entityType);
    const targetEntityType = entityType === EntityType.ACTIVITY ? entityType : EntityType.PIN;
    return state[targetEntityType].list.filter(
      (filter) => favoriteFilterIds.includes(filter.id) && isSuitable(filter)
    ).length;
  }
);

export const isSavedFiltersLoading = createSelector(
  [savedFiltersState, (_, entityType: EntityTypesSupportingSavedFilters) => entityType],
  (state, entityType) =>
    state[entityType === EntityType.ACTIVITY ? entityType : EntityType.PIN].loading
);

export const getSelectedFilters = createSelector(savedFiltersState, (state) =>
  (
    [
      EntityType.ACTIVITY,
      EntityType.COMPANY,
      EntityType.DEAL,
      EntityType.PERSON,
      EntityType.PIN,
    ] as const
  ).reduce(
    (result, entityType) =>
      Object.assign(result, { [entityType]: state[entityType].selectedFilter }),
    {} as Record<EntityTypesSupportingSavedFilters, SavedFilter | undefined>
  )
);

export const getSelectedFilter = createSelector(
  savedFiltersState,
  getSelectedFilters,
  (state, selectedFilters) =>
    (entityType: EntityType): SavedFilter | undefined => {
      return selectedFilters[entityType as EntityTypesSupportingSavedFilters];
    }
);

export const isSavedFilterCreateLoading = createSelector(
  savedFiltersState,
  ({ createLoading }) => createLoading
);

export const isSavedFilterUpdateLoading = createSelector(
  savedFiltersState,
  ({ updateLoading }) => updateLoading
);

export const isSavedFilterDeleteLoading = createSelector(
  savedFiltersState,
  ({ deleteLoading }) => deleteLoading
);
